/**
 * The adaptive placement test (§6).
 *
 * The first question a new student is asked must never be "what grade are
 * you?" — it is "let's find your math level". The test walks the concept graph:
 * a correct answer moves up towards harder concepts, a wrong answer moves down
 * into that concept's prerequisites, exactly as a tutor would probe.
 *
 * The state machine is pure and serialisable, so a half-finished diagnostic
 * survives a page reload and can be replayed in a test.
 */

import type { CurriculumRegistry, LevelId } from '@yuan-math/curriculum';
import { allPrerequisites, topologicalOrder } from '@yuan-math/curriculum';
import type { ExerciseBank, Exercise, GradeResult } from '@yuan-math/exercises';
import type { DiagnosticSummary } from './types.ts';

export type DiagnosticState = {
  /** Concepts asked about so far, in order. */
  askedConceptIds: string[];
  /** concept id → did the student get it right. */
  results: Record<string, boolean>;
  /** Concepts still worth probing, best-first. */
  queue: string[];
  /** Concepts ruled out — already implied by another answer. */
  resolved: string[];
  /** The climb this test started with, one concept per grade. */
  rungs?: string[];
  /** Rungs missed in a row. Two is a wall; one is a slip. */
  consecutiveMisses?: number;
  currentConceptId?: string;
  currentExercise?: Exercise;
  finished: boolean;
  seed: string;
};

export const MAX_DIAGNOSTIC_QUESTIONS = 12;

export type DiagnosticOptions = {
  registry: CurriculumRegistry;
  bank: ExerciseBank;
  /** Restricts the probe to concepts the student plausibly knows about. */
  startLevel?: LevelId;
  seed?: string;
};

/**
 * How many rungs the climb has. Nine leaves room inside the twelve-question
 * budget for the descent a wrong answer triggers, and matches what the page
 * promises the student ("around ten questions").
 */
export const DIAGNOSTIC_LADDER_RUNGS = 9;

/**
 * Chooses the probe order: a *ladder* of one concept per grade, climbing.
 *
 * Probing every concept would take an hour, so the test samples. Ordered by the
 * level a concept is typically taught at, not by topological order. Both are
 * valid orderings of the graph, but they are different: a topological sort put
 * Dividing Fractions (Grade 6) before Simplifying Fractions (Grade 4), which is
 * legal — the prerequisites are satisfied — and bewildering to sit through. A
 * placement test should feel like climbing, and an early failure should bound
 * the level rather than merely fail.
 *
 * Two things decide a rung, in this order:
 *
 * **The grade**, so the climb is even. Sampling evenly from the list of
 * concepts instead — the first version of this — is not the same thing, because
 * the grades are not evenly stocked: thirty-nine geometry concepts sit at Grade
 * 10 and one at Grade 12, so an even walk through the concepts bunches at the
 * crowded grades and skips whole years. Even *rungs* is what makes the answer
 * "you are somewhere around Grade 9" rather than "you are somewhere".
 *
 * **The strand**, so the climb is wide. Each rung prefers a strand no rung has
 * used yet, which is what turns the result into a profile of a student rather
 * than a single line drawn through the curriculum. The first version walked a
 * line: six questions, and eleven of the twenty-one strands went unmentioned in
 * the profile it printed.
 *
 * Topological position breaks the last tie, so the ladder stays deterministic
 * and never places a concept before something it depends on.
 */
export function diagnosticSpine(options: DiagnosticOptions, count = DIAGNOSTIC_LADDER_RUNGS): string[] {
  const { registry, bank } = options;

  const topological = topologicalOrder(registry.graph);
  const positionOf = new Map(topological.map((id, index) => [id, index]));
  const levelOrder = new Map(registry.levels.map((level) => [level.id, level.order]));

  /** The earliest level a concept is taught at — how hard it feels to a student. */
  const difficultyOf = (id: string): number => {
    const levels = (registry.concept(id)?.levels ?? [])
      .map((levelId) => levelOrder.get(levelId))
      .filter((value): value is number => value !== undefined);
    return levels.length > 0 ? Math.min(...levels) : 0;
  };

  const testable = topological.filter((id) => registry.concept(id)?.status === 'available' && bank.hasContent(id));
  if (testable.length <= count) return [...testable].sort((a, b) => difficultyOf(a) - difficultyOf(b));

  /*
   * The climb stops at the top of school, not at the top of the curriculum.
   *
   * Those were the same thing while the hardest teachable concept was a Grade
   * 12 one, so the ceiling was simply inferred from the content. They stopped
   * being the same thing the moment a university branch was built: a single
   * university rung displaced two school ones — Grade 5 and Grade 10 dropped
   * out of a nine-rung ladder — and the top question became linear algebra.
   *
   * The output of this test is a `LevelId` used to tell a student they are
   * around Grade 9. It cannot usefully report "around university", and one
   * right answer about eigenvectors would not be the evidence for that claim
   * even if it could. So the rungs are drawn from school levels only, and the
   * university branches are reached the way every other advanced topic is —
   * by working up the graph, not by being guessed at from one question.
   */
  const schoolCeiling = Math.max(
    ...registry.levels.filter((level) => level.stage !== 'university').map((level) => level.order),
  );
  const inScope = testable.filter((id) => difficultyOf(id) <= schoolCeiling);
  // A curriculum made entirely of university content would leave nothing to
  // ask, and a ladder with no rungs is worse than one that reaches too high.
  const pool = inScope.length >= count ? inScope : testable;

  // The grades that actually have something to ask about, low to high.
  const byLevel = new Map<number, string[]>();
  for (const id of pool) {
    const level = difficultyOf(id);
    byLevel.set(level, [...(byLevel.get(level) ?? []), id]);
  }
  const levels = [...byLevel.keys()].sort((a, b) => a - b);

  // Evenly spaced rungs, always including the floor and the ceiling: the lowest
  // grade is the student who needs somewhere to land, the highest is the only
  // way a strong student is ever measured rather than merely not contradicted.
  const rungLevels: number[] = [];
  for (let i = 0; i < count; i += 1) {
    const level = levels[Math.round((i / (count - 1)) * (levels.length - 1))] as number;
    if (!rungLevels.includes(level)) rungLevels.push(level);
  }

  const usedStrands = new Set<string>();
  const spine: string[] = [];

  for (const level of rungLevels) {
    const candidates = (byLevel.get(level) ?? []).filter((id) => !spine.includes(id));
    if (candidates.length === 0) continue;

    const pick = candidates.reduce((best, id) => {
      const fresh = (candidateId: string): number =>
        usedStrands.has(registry.concept(candidateId)?.strand ?? '') ? 1 : 0;
      if (fresh(id) !== fresh(best)) return fresh(id) < fresh(best) ? id : best;
      return (positionOf.get(id) ?? 0) < (positionOf.get(best) ?? 0) ? id : best;
    });

    usedStrands.add(registry.concept(pick)?.strand ?? '');
    spine.push(pick);
  }

  return spine;
}

export function startDiagnostic(options: DiagnosticOptions): DiagnosticState {
  const seed = options.seed ?? 'diagnostic';
  const queue = diagnosticSpine(options);
  const state: DiagnosticState = {
    askedConceptIds: [],
    results: {},
    queue,
    resolved: [],
    rungs: [...queue],
    consecutiveMisses: 0,
    finished: queue.length === 0,
    seed,
  };
  return nextQuestion(state, options);
}

/** Advances to the next probe, or marks the diagnostic finished. */
export function nextQuestion(state: DiagnosticState, options: DiagnosticOptions): DiagnosticState {
  if (state.askedConceptIds.length >= MAX_DIAGNOSTIC_QUESTIONS) {
    return { ...state, finished: true, ...clearCurrent() };
  }

  const queue = [...state.queue];
  while (queue.length > 0) {
    const conceptId = queue.shift() as string;
    if (state.askedConceptIds.includes(conceptId) || state.resolved.includes(conceptId)) continue;

    // Mid-range difficulty: a placement probe should be typical of the concept,
    // not its hardest or easiest corner.
    const exercise = options.bank.single(conceptId, 2, `${state.seed}:${conceptId}`);
    if (!exercise) continue;

    return { ...state, queue, currentConceptId: conceptId, currentExercise: exercise, finished: false };
  }

  return { ...state, queue, finished: true, ...clearCurrent() };
}

const clearCurrent = (): Pick<DiagnosticState, 'currentConceptId' | 'currentExercise'> => ({
  currentConceptId: undefined,
  currentExercise: undefined,
});

/**
 * Records an answer and re-plans.
 *
 * Correct: everything underneath is implied, so prerequisites are resolved
 * without being asked — this is what keeps the test to a dozen questions.
 *
 * Wrong: the prerequisites become the next thing to probe, because the failure
 * is more likely to live below the concept than at it.
 */
export function answerDiagnostic(
  state: DiagnosticState,
  result: GradeResult,
  options: DiagnosticOptions,
): DiagnosticState {
  const conceptId = state.currentConceptId;
  if (!conceptId) return state;
  if (result.status === 'malformed') return state;

  const correct = result.correct;
  const prerequisites = allPrerequisites(options.registry.graph, conceptId);

  const resolved = new Set(state.resolved);
  const results = { ...state.results, [conceptId]: correct };
  let queue = [...state.queue];

  const rungs = state.rungs ?? [];
  const onLadder = rungs.includes(conceptId);
  let consecutiveMisses = state.consecutiveMisses ?? 0;
  if (onLadder) consecutiveMisses = correct ? 0 : consecutiveMisses + 1;

  if (correct) {
    for (const prereqId of prerequisites) {
      if (results[prereqId] === undefined) {
        results[prereqId] = true;
        resolved.add(prereqId);
      }
    }
    queue = queue.filter((id) => !resolved.has(id));
  } else {
    // Probe the nearest prerequisites first — `allPrerequisites` is breadth-first.
    const probes = prerequisites
      .filter((id) => results[id] === undefined && options.bank.hasContent(id))
      .slice(0, 2);
    queue = [...probes, ...queue.filter((id) => !probes.includes(id))];

    /*
     * Two rungs missed in a row is a wall, and there is nothing above a wall
     * worth asking. Marching a Grade 4 student on up to the Grade 11 rung
     * spends the question budget proving something already known and reads, to
     * the student sitting there, as a test designed to humiliate them. The
     * questions are better spent below, where the answer actually is.
     *
     * Two, not one, and that distinction is load-bearing: a single miss is a
     * slip, and truncating the climb on a slip is how a student who could go on
     * to answer inequalities, sequences and trig graphs gets stopped at Grade 3
     * rounding and placed there. One rung above the wall is kept, because the
     * grade boundary itself is approximate.
     */
    if (onLadder && consecutiveMisses >= 2) {
      const ceiling = levelOf(options, conceptId) + 1;
      queue = queue.filter((id) => !rungs.includes(id) || levelOf(options, id) <= ceiling);
    }
  }

  const advanced: DiagnosticState = {
    ...state,
    askedConceptIds: [...state.askedConceptIds, conceptId],
    results,
    resolved: [...resolved],
    consecutiveMisses,
    queue,
  };

  return nextQuestion(advanced, options);
}

/** The earliest grade a concept is taught at, as a number. */
function levelOf(options: DiagnosticOptions, conceptId: string): number {
  const orders = (options.registry.concept(conceptId)?.levels ?? [])
    .map((levelId) => options.registry.level(levelId)?.order)
    .filter((value): value is number => value !== undefined);
  return orders.length > 0 ? Math.min(...orders) : 0;
}

export function progressOf(state: DiagnosticState): { asked: number; total: number; fraction: number } {
  const asked = state.askedConceptIds.length;
  const total = Math.min(MAX_DIAGNOSTIC_QUESTIONS, asked + state.queue.length + (state.finished ? 0 : 1));
  return { asked, total: Math.max(total, 1), fraction: asked / Math.max(total, 1) };
}

/**
 * Turns the answers into the profile shown at the end of the test — the
 * strand bars and the recommended starting point (§6, §27).
 */
export function summarizeDiagnostic(
  state: DiagnosticState,
  options: DiagnosticOptions,
  now: number,
): DiagnosticSummary {
  const { registry } = options;

  /*
   * The strand bars.
   *
   * Scored against the *strand*, not against the handful of questions that
   * touched it, and that is the whole difference between a profile and a row of
   * full bars. Dividing correct answers by asked answers is a ratio of a test to
   * itself: every implied prerequisite is recorded as correct, so a student who
   * answered six questions well printed eleven bars each reading exactly 100%,
   * and the ten strands nobody had asked about were left off the card
   * altogether. Nothing there described the student.
   *
   * So the denominator is what the course teaches in that strand, and every
   * strand is reported — including the ones the test never reached, which is a
   * fact about the test worth admitting rather than hiding. `strandCoverage`
   * carries that admission: it is how much of the strand we have any evidence
   * about at all, so a bar at 0.4 out of 0.9 coverage ("we looked, and this is
   * where you are") can be told apart from 0.4 out of 0.4 ("we barely looked").
   */
  const strandScores: Record<string, number> = {};
  const strandCoverage: Record<string, number> = {};

  for (const strand of registry.strands) {
    const concepts = registry.conceptsInStrand(strand.id).filter((concept) => concept.status === 'available');
    if (concepts.length === 0) continue;
    const seen = concepts.filter((concept) => state.results[concept.id] !== undefined);
    strandScores[strand.id] = seen.filter((concept) => state.results[concept.id] === true).length / concepts.length;
    strandCoverage[strand.id] = seen.length / concepts.length;
  }

  const assumedMastered = Object.entries(state.results)
    .filter(([, wasCorrect]) => wasCorrect)
    .map(([conceptId]) => conceptId);

  // What they were actually asked and got right, as opposed to what that
  // implied. The profile keeps the two apart because they are different
  // evidence, and only the first one is a measurement.
  const demonstrated = state.askedConceptIds.filter((conceptId) => state.results[conceptId] === true);

  const order = topologicalOrder(registry.graph);
  const isAvailable = (id: string): boolean => registry.concept(id)?.status === 'available';

  // The earliest genuine gap: the first concept in teaching order they got
  // wrong. Earliest rather than hardest, because a failure low in the graph
  // explains every failure above it.
  const firstFailure = order.find((id) => state.results[id] === false && isAvailable(id));

  const recommendedLevel = levelFromResults(state, options, firstFailure);

  /*
   * With nothing wrong, the starting point is the frontier — a concept they
   * have not demonstrated whose prerequisites they *have*.
   *
   * The "whose prerequisites they have" clause is the whole point. Without it
   * this picked the first untested concept in teaching order, which is
   * typically an easy one from a branch the test never probed, and a student
   * who answered everything correctly was told to start at Grade 2.
   */
  const frontier = frontierConcept(state, options, order, recommendedLevel);

  const startConceptId = firstFailure ?? frontier;

  // True when they got everything right that we were able to ask. Worth
  // reporting honestly rather than implying we found their ceiling.
  const answeredEverythingCorrectly =
    state.askedConceptIds.length > 0 && !Object.values(state.results).includes(false);

  return {
    completedAt: now,
    strandScores,
    strandCoverage,
    assumedMastered,
    demonstrated,
    recommendedLevel,
    answeredEverythingCorrectly,
    ...(startConceptId ? { startConceptId } : {}),
  };
}

/**
 * Picks where to start, when the test found no gap to repair.
 *
 * Every candidate here is ready to be learned — prerequisites proved, concept
 * not yet demonstrated — so the choice among them is about *level*, and taking
 * the first in teaching order got that badly wrong: it sent a student who had
 * just answered a Grade 11 question to Division with Remainders, because the
 * easy branches a six-question test never probes all sit at the front of the
 * ordering. Twenty-nine concepts qualified; the one chosen was the lowest.
 */
function frontierConcept(
  state: DiagnosticState,
  options: DiagnosticOptions,
  order: string[],
  recommendedLevel: LevelId,
): string | undefined {
  const { registry } = options;
  const orderOf = new Map(registry.levels.map((level) => [level.id, level.order]));
  const target = orderOf.get(recommendedLevel) ?? 0;

  const levelOf = (conceptId: string): number => {
    const values = (registry.concept(conceptId)?.levels ?? [])
      .map((levelId) => orderOf.get(levelId))
      .filter((value): value is number => value !== undefined);
    return values.length > 0 ? Math.min(...values) : 0;
  };

  // Teaching order, so that ties fall to the earliest concept.
  const candidates = order.filter(
    (id) =>
      state.results[id] === undefined &&
      registry.concept(id)?.status === 'available' &&
      (registry.concept(id)?.prerequisites ?? []).every((prereq) => state.results[prereq] === true),
  );
  if (candidates.length === 0) return undefined;

  // At the level they were placed at where possible, otherwise as close below
  // it as the graph allows.
  const atOrAbove = candidates.filter((id) => levelOf(id) >= target);
  const pool = atOrAbove.length > 0 ? atOrAbove : candidates;

  return pool.reduce((best, id) =>
    Math.abs(levelOf(id) - target) < Math.abs(levelOf(best) - target) ? id : best,
  );
}

/**
 * Picks a level.
 *
 * Driven by what the student *demonstrated*, never by a concept they were
 * never asked about. That distinction was a real bug: a student who answered
 * everything correctly was placed at Grade 2, because the level was read off
 * the first untested concept in teaching order rather than off their answers.
 *
 * The level is the highest thing they got right, and a gap lower down does not
 * pull it back down. Letting the earliest failure set the level was the second
 * version of the same bug: one slip on Grade 3 rounding placed a student at
 * Grade 3 even though they had just answered inequalities, sequences and trig
 * graphs correctly — five harder answers discarded by one easy miss. An
 * isolated gap is a repair item, and `startConceptId` already carries it there.
 *
 * Only when they demonstrated nothing at all does the breakdown set the level,
 * because then it is the sole evidence there is.
 */
function levelFromResults(
  state: DiagnosticState,
  options: DiagnosticOptions,
  firstFailure: string | undefined,
): LevelId {
  const { registry } = options;
  const levels = registry.levels;
  const orderOf = new Map(levels.map((level) => [level.id, level.order]));

  const levelsOf = (conceptId: string): number[] =>
    (registry.concept(conceptId)?.levels ?? [])
      .map((levelId) => orderOf.get(levelId))
      .filter((value): value is number => value !== undefined);

  // Asked and answered, not implied. A correct answer marks its prerequisites
  // true without asking, and a handful of those are placed a grade *above* the
  // concept they support — enough to award a level nobody measured.
  const demonstrated = state.askedConceptIds
    .filter((conceptId) => state.results[conceptId] === true)
    .flatMap(levelsOf);

  let candidate: number;

  if (demonstrated.length > 0) {
    // They are at least as far along as the hardest thing they got right;
    // claiming more would be inventing a measurement.
    candidate = Math.max(...demonstrated);
  } else if (firstFailure) {
    // Nothing right at all, so the breakdown is all we know. The lowest level
    // that concept is taught at, since that is where the teaching of it begins.
    const failureLevels = levelsOf(firstFailure);
    candidate = failureLevels.length > 0 ? Math.min(...failureLevels) : 0;
  } else {
    candidate = 0;
  }

  /*
   * Placing a student too high is far more damaging than placing them slightly
   * low, so a mostly-wrong test leans down one level.
   *
   * "Mostly wrong" is judged on the questions at or below the candidate level,
   * not on the whole test, and the difference matters now that the test climbs
   * to the top of the curriculum. Every student has a ceiling, so every student
   * ends up answering some questions above it — and counting those against them
   * punishes them for the test having gone on climbing. Scored over everything,
   * a student who answered five questions correctly up to Grade 4 and then
   * missed seven harder ones came out at 42% and was pushed down to Grade 3,
   * below work they had just done twice.
   *
   * Below the candidate is where a placement can actually be wrong: misses
   * there mean the correct answers above them are not holding up the level.
   */
  const atOrBelow = state.askedConceptIds.filter((id) => {
    const levels = levelsOf(id);
    return levels.length === 0 || Math.min(...levels) <= candidate;
  });
  const supported = atOrBelow.filter((id) => state.results[id] === true).length;
  const shaky = atOrBelow.length > 0 && supported / atOrBelow.length < 0.5;
  const adjusted = shaky ? Math.max(0, candidate - 1) : candidate;

  const level = levels.find((entry) => entry.order === adjusted) ?? levels[0];
  return (level?.id ?? 'grade-3') as LevelId;
}
